import * as React from 'react'

import { ChevronUpIcon } from 'lucide-react'

import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import NextDayLabel from './NextDayLabel'
import TimeCombobox from './TimeCombobox'

interface IProps {
  startMinutes: number
  endMinutes: number
  projectDateStore: ProjectDateStore
  onChange: (startMinutes: number, endMinutes: number) => void
  ariaLabel: string
  isDisabled?: boolean
  portalContainer?: React.ComponentProps<
    typeof PopoverContent
  >['portalContainer']
}

const MINUTES_IN_HOUR = 60

/**
 * One weekday's window, shown as a select and edited in a small popover.
 *
 * Deliberately not `TimeRangeField`, which opens a whole `IntervalEditor`: this
 * row already lives inside one, and nesting would put a second set of Daily/Date
 * tabs inside the tabs the user is already in. Only the two times are offered —
 * the weekday is the row, and the Daily/Date choice belongs to the editor.
 */
function DayWindowField({
  startMinutes,
  endMinutes,
  projectDateStore,
  onChange,
  ariaLabel,
  isDisabled,
  portalContainer,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const toDisplay = (minutes: number): string => {
    const time = projectDateStore.setHours(
      new Date(),
      Math.floor(minutes / MINUTES_IN_HOUR),
      minutes % MINUTES_IN_HOUR,
    )
    return projectDateStore.getTimeToDisplay(time)
  }

  // The end may sit at or before the start, which is the overnight window rather
  // than an invalid one — the same reading the Daily tab gives a single window.
  const endsNextDay = endMinutes <= startMinutes

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={isDisabled}
        aria-label={ariaLabel}
        render={<FieldTrigger icon={isOpen ? <ChevronUpIcon /> : undefined} />}
      >
        {`${toDisplay(startMinutes)} - ${toDisplay(endMinutes)}`}
        {endsNextDay && <NextDayLabel />}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Matches the trigger, the way Select and Combobox popups do here, so the
        // two stay the same width without the row's column size being repeated.
        className="tw:w-(--anchor-width) tw:p-3"
        portalContainer={portalContainer}
        // Pointer-opening should not drop the caret into Start time and select
        // its text; the window is there to be read first.
        initialFocus={openType => openType === 'keyboard'}
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:text-sm tw:font-medium tw:text-foreground">
              {Localization.translator.startTime}
            </span>
            <TimeCombobox
              value={startMinutes}
              onChange={minutes => onChange(minutes, endMinutes)}
              projectDateStore={projectDateStore}
              ariaLabel={Localization.translator.startTime}
              portalContainer={portalContainer}
            />
          </div>
          <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:text-sm tw:font-medium tw:text-foreground">
              {Localization.translator.endTime}
            </span>
            <TimeCombobox
              value={endMinutes}
              onChange={minutes => onChange(startMinutes, minutes)}
              projectDateStore={projectDateStore}
              ariaLabel={Localization.translator.endTime}
              // Reads as a duration from the start, so an overnight window is
              // offered rather than looking like a backwards one.
              fromMinutes={startMinutes}
              portalContainer={portalContainer}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DayWindowField
